import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Typography,
  Box,
} from "@mui/material";
import { useGetAllCompaniesQuery } from "../../../../../redux-toolkit/features/company/companyApiSlice";
import {
  useAssignCompaniesToAdminMutation,
  useGetAdminManagedCompaniesQuery,
} from "../../../../../redux-toolkit/features/rbac/rbacApiSlice";
import { useAlert } from "../../../../../hooks/useAlert";

export const CompanyAssignmentModal = ({
  open,
  onClose,
  adminId,
  adminName,
  currentManagedCompanies = [],
}) => {
  const { displayAlert } = useAlert();
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch all companies
  const { data: companiesData, isLoading: companiesLoading } =
    useGetAllCompaniesQuery();

  // Fetch current managed companies for this admin
  const { data: adminCompaniesData, isLoading: adminCompaniesLoading } =
    useGetAdminManagedCompaniesQuery(adminId, {
      skip: !adminId || !open,
    });

  const [assignCompanies] = useAssignCompaniesToAdminMutation();

  // Initialize selected companies when modal opens or data changes
  useEffect(() => {
    if (open && adminCompaniesData?.data) {
      const managedCompanyIds =
        adminCompaniesData.data.managedCompanies?.map((c) => c._id) || [];
      setSelectedCompanies(managedCompanyIds);
    } else if (open && currentManagedCompanies.length > 0) {
      // Fallback to prop if API data not available
      const companyIds = currentManagedCompanies.map((c) =>
        c._id ? c._id : c
      );
      setSelectedCompanies(companyIds);
    } else if (open) {
      setSelectedCompanies([]);
    }
  }, [open, adminCompaniesData, currentManagedCompanies]);

  const handleCompanyToggle = (companyId) => {
    setSelectedCompanies((prev) => {
      if (prev.includes(companyId)) {
        return prev.filter((id) => id !== companyId);
      } else {
        return [...prev, companyId];
      }
    });
  };

  const handleSave = async () => {
    if (!adminId) {
      displayAlert({
        open: true,
        message: "Admin ID is required",
        type: "error",
        timeout: 1500,
      });
      return;
    }

    setIsLoading(true);
    try {
      await assignCompanies({
        userId: adminId,
        companyIds: selectedCompanies,
      }).unwrap();

      displayAlert({
        open: true,
        message: "Companies assigned successfully",
        type: "success",
        timeout: 1500,
      });
      onClose();
      // Reset selection
      setSelectedCompanies([]);
    } catch (error) {
      displayAlert({
        open: true,
        message: error?.data?.message || "Failed to assign companies",
        type: "error",
        timeout: 1500,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setSelectedCompanies([]);
    onClose();
  };

  const companies = companiesData?.data?.companies || [];
  const primaryCompany = adminCompaniesData?.data?.primaryCompany;
  
  // Get primary company ID (handle both object and string formats)
  const primaryCompanyId = primaryCompany?._id 
    ? primaryCompany._id.toString() 
    : primaryCompany?.toString();

  // Filter out primary company from selectable companies
  const selectableCompanies = companies.filter((company) => {
    if (!primaryCompanyId) return true;
    const companyId = company._id?.toString() || company._id;
    return companyId !== primaryCompanyId;
  });

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="md" fullWidth>
      <DialogTitle>
        Assign Companies to {adminName || "Admin"}
      </DialogTitle>
      <DialogContent>
        {companiesLoading || adminCompaniesLoading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : (
          <Box>
            <FormControl component="fieldset" fullWidth>
              <FormLabel component="legend">Select Companies to Manage</FormLabel>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 0.5, mb: 1.5, lineHeight: 1.5 }}
              >
                <Box component="span" sx={{ fontWeight: 600 }}>
                  Note:
                </Box>{" "}
                Roles are inherited from primary company
              </Typography>
              <FormGroup>
                {/* Show primary company as disabled, always checked */}
                {primaryCompany && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={true}
                        disabled={true}
                        sx={{
                          "&.Mui-disabled": {
                            color: "primary.main",
                          },
                        }}
                      />
                    }
                    label={
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 500,
                          color: "text.primary",
                        }}
                      >
                        {primaryCompany.name || "Primary Company"} (Primary Company)
                      </Typography>
                    }
                    sx={{
                      opacity: 0.7,
                      mb: 1,
                      pb: 1,
                      borderBottom: "1px solid",
                      borderColor: "divider",
                      "& .MuiFormControlLabel-label": {
                        cursor: "default",
                      },
                    }}
                  />
                )}

                {/* Show selectable companies */}
                {selectableCompanies.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" p={2}>
                    No additional companies available
                  </Typography>
                ) : (
                  selectableCompanies.map((company) => (
                    <FormControlLabel
                      key={company._id}
                      control={
                        <Checkbox
                          checked={selectedCompanies.includes(company._id)}
                          onChange={() => handleCompanyToggle(company._id)}
                        />
                      }
                      label={company.name}
                    />
                  ))
                )}
              </FormGroup>
            </FormControl>

            {/* Show count of selected companies */}
            {selectedCompanies.length > 0 && (
              <Box mt={2}>
                <Typography variant="body2" color="text.secondary">
                  {selectedCompanies.length} additional compan
                  {selectedCompanies.length === 1 ? "y" : "ies"} selected
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={20} /> : null}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
